import { useMemo, useState, RefObject, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import ExportIcon from '~/shared/assets/icons/export.svg';
import { Account, ReportSelectionOption, SelectOption } from '~/shared/utils/types';
import ChartFilterDropdown from '~/shared/components/ChartFilterDropdown';
import Button from '~/shared/components/ButtonsV2';
import { LineChartHandle } from '~/shared/components/LineChart/LineChart';
import { useExportState } from '~/shared/context/ExportStateContext';
import { safeJsonParse, safeJsonParseArray } from '~/shared/utils/safeJson';

import { CUSTOM_DATES_TITLE, dateFilterItems } from '../common/dateFilterMenuItems';
import { apiFilterKey } from '../common/serachParamKeys';
import ReportsExportModal from '../ReportsPage/components/ReportsExportModal';

import ReportsFiltersSidePanel from './FiltersSidePanel/ReportsFiltersSidePanel';
import { AUTOSCALING_STATE_PARAM_KEY } from './FiltersSidePanel/utils/filterUtils';
import { useFilterUrlParams } from './FiltersSidePanel/hooks/useFilterUrlParams';

interface ReportsFilterSectionProps {
  activeDateFilterTitle: string | null;
  currentBaseFilters: ReportSelectionOption[];
  onDateFilterChange: (filter: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  }) => void;
  onBaseFiltersChange: (newFilters: ReportSelectionOption[]) => void;
  setPageOffset: (offset: string) => void;
  utilizationChartRef: RefObject<LineChartHandle | null>;
  usageChartRef: RefObject<LineChartHandle | null>;
  tableQueryUrl: string;
  tableCountUrl: string;
  chartQueryUrl: string;
  totalCount: number | undefined;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
}

export default function ReportsFilterSection({
  activeDateFilterTitle,
  onDateFilterChange,
  setPageOffset,
  utilizationChartRef,
  usageChartRef,
  tableQueryUrl,
  tableCountUrl,
  chartQueryUrl,
  totalCount,
  isOrgView = false,
  accounts = [],
  activeOrgId,
}: ReportsFilterSectionProps) {
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { isExporting } = useExportState();
  const reportsExporting = isExporting('reports');
  const [searchParams] = useSearchParams();
  const apiFilters = searchParams.get(apiFilterKey);
  const { resetFilters } = useFilterUrlParams();

  const currentBaseFilters = useMemo(
    () => safeJsonParseArray<SelectOption>(apiFilters),
    [apiFilters]
  );

  const {
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAccountFilters,
  } = useMemo(() => {
    const processedFilters = searchParams.get('processed_filters');
    const appliedClusterNameFilters: SelectOption[] = [];
    const appliedInstanceFilters: SelectOption[] = [];
    const appliedVolumeIdFilters: SelectOption[] = [];
    const appliedVolumeTagFilters: SelectOption[] = [];
    const appliedAccountFilters: SelectOption[] = [];

    const TAG_FIELD_RE = /^tag:(.+)$/;
    const VALUE_SPLIT_RE = /\s*,\s*/;

    if (processedFilters) {
      const parsedProcessedFilters = safeJsonParseArray<unknown>(processedFilters);

      parsedProcessedFilters.forEach((filterEntry) => {
        const filter =
          typeof filterEntry === 'string'
            ? safeJsonParse<Record<string, unknown> | null>(filterEntry, null)
            : (filterEntry as Record<string, unknown> | null);
        if (!filter) {
          return;
        }

        const { field, value, op } = filter;

        if (
          typeof field === 'string' &&
          (op === 'in' || op === 'equals') &&
          typeof value === 'string'
        ) {
          const values = value.split(VALUE_SPLIT_RE);
          const tagMatch = TAG_FIELD_RE.exec(field);

          values.forEach((val: string) => {
            const trimmed = val.trim();
            if (!trimmed) {
              return;
            }

            if (field === 'clusterName') {
              appliedClusterNameFilters.push({ id: trimmed, name: trimmed, group: 'clusterName' });
            } else if (field === 'instanceId') {
              appliedInstanceFilters.push({ id: trimmed, name: trimmed, group: 'instanceId' });
            } else if (field === 'volumeId') {
              appliedVolumeIdFilters.push({ id: trimmed, name: trimmed, group: 'volumeId' });
            } else if (field === 'accountId') {
              appliedAccountFilters.push({ id: trimmed, name: trimmed, group: 'accountId' });
            } else if (tagMatch) {
              const key = tagMatch[1];
              const name = key && trimmed ? `${key}:${trimmed}` : key || trimmed;
              appliedVolumeTagFilters.push({ id: name, name, group: `tag:${key}` as const });
            }
          });
        }
      });
    } else if (apiFilters) {
      appliedClusterNameFilters.push(
        ...currentBaseFilters.filter((f) => f.group === 'clusterName')
      );
      appliedInstanceFilters.push(...currentBaseFilters.filter((f) => f.group === 'instanceId'));
      appliedVolumeIdFilters.push(...currentBaseFilters.filter((f) => f.group === 'volumeId'));
      appliedAccountFilters.push(...currentBaseFilters.filter((f) => f.group === 'accountId'));
      appliedVolumeTagFilters.push(
        ...currentBaseFilters.filter(
          (f) => typeof f.group === 'string' && (f.group as string).startsWith('tag:')
        )
      );
    }

    return {
      appliedClusterNameFilters,
      appliedInstanceFilters,
      appliedVolumeIdFilters,
      appliedVolumeTagFilters,
      appliedAccountFilters,
    };
  }, [searchParams, currentBaseFilters, apiFilters]);

  const appliedAutoscaleState = useMemo(
    () =>
      (searchParams.get(AUTOSCALING_STATE_PARAM_KEY) || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean),
    [searchParams]
  );

  const activeFilterCount = useMemo(() => {
    const autoscalingCount = appliedAutoscaleState.reduce((acc, id) => {
      if (id === 'as_state_on' || id === 'as_state_off' || id === 'as_state_unsupported') {
        return acc + 1;
      }
      return acc;
    }, 0);
    const baseFilterCount = currentBaseFilters.filter((f) => f.group !== 'autoscalingState').length;
    return baseFilterCount + autoscalingCount;
  }, [currentBaseFilters, appliedAutoscaleState]);

  const activeDateFilterSelectedItem: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  } = (() => {
    if (activeDateFilterTitle && activeDateFilterTitle.includes(CUSTOM_DATES_TITLE)) {
      return { title: activeDateFilterTitle };
    }
    const foundItem = dateFilterItems.find((item) => item.title === activeDateFilterTitle);
    return (
      foundItem ||
      dateFilterItems.find((item) => item.title === 'Last 3 hours') ||
      dateFilterItems[0] || { title: 'Error: No Date Filter' }
    );
  })();

  const handleResetFilters = useCallback(() => {
    setPageOffset('0');
    resetFilters();
    setSidePanelOpen(false);
  }, [resetFilters, setPageOffset]);

  return (
    <div className="px-[30px] py-[20px] flex justify-between gap-4 ">
      <ChartFilterDropdown
        align="top-left"
        items={dateFilterItems}
        onSelect={onDateFilterChange}
        selectedItem={activeDateFilterSelectedItem}
      />
      <div className="ml-auto flex gap-2 z-30 items-center">
        <Button
          variant="third"
          onClick={() => setIsExportModalOpen(true)}
          className="w-10"
          size="md-responsive"
          loading={reportsExporting}
          disabled={reportsExporting}
          aria-label="Export reports data"
          icon={<ExportIcon className="size-5" />}
        />
        <ReportsFiltersSidePanel
          open={sidePanelOpen}
          onOpenChange={setSidePanelOpen}
          onResetFilters={handleResetFilters}
          appliedClusterNameFilters={appliedClusterNameFilters}
          appliedInstanceFilters={appliedInstanceFilters}
          appliedVolumeIdFilters={appliedVolumeIdFilters}
          appliedVolumeTagFilters={appliedVolumeTagFilters}
          appliedAccountFilters={appliedAccountFilters}
          appliedAutoscaleState={appliedAutoscaleState}
          activeFilterCount={activeFilterCount}
          setSidePanelOpen={setSidePanelOpen}
          isSidePanelOpen={sidePanelOpen}
          isOrgView={isOrgView}
          accounts={accounts}
          activeOrgId={activeOrgId}
        />
      </div>

      <ReportsExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        activeFilters={currentBaseFilters}
        utilizationChartRef={utilizationChartRef}
        usageChartRef={usageChartRef}
        tableQueryUrl={tableQueryUrl}
        tableCountUrl={tableCountUrl}
        chartQueryUrl={chartQueryUrl}
        activeDateFilterTitle={activeDateFilterTitle}
      />
    </div>
  );
}
